"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Standard() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".pairing-image", {
        scale: 1.04
      });
      gsap.to(".pairing-image", {
        opacity: 1,
        scale: 1,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".pairing-aura", {});
      gsap.to(".pairing-aura", {
        opacity: 0.22,
        duration: 6,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true
      });
      gsap.set(".pairing-reveal", {
        y: 40
      });
      gsap.to(".pairing-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.12,
        delay: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.to(".pairing-italic-glow", {
        opacity: 1,
        duration: 0.6,
        delay: 1.0,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".pairing-image", ".pairing-reveal", ".pairing-italic-glow"], {
        opacity: 1,
        y: 0,
        scale: 1
      });
      gsap.set(".pairing-aura", {
        opacity: 0.15
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="28503" id="the-gold-standard-el-estándar-de-oro" data-nav="dark" ref={container} className="relative overflow-hidden bg-background text-foreground py-32 md:py-44">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {}
          <div className="relative lg:col-span-6">
            {}
            <div className="pairing-aura pointer-events-none absolute -top-24 -right-24 h-[500px] w-[500px] md:h-[700px] md:w-[700px] rounded-lg" style={{
            opacity: 0.12,
            background: "radial-gradient(ellipse at center, hsl(41 62% 62% / 0.5) 0%, transparent 65%)",
            filter: "blur(20px)"
          }} aria-hidden="true" />

            <div className="pairing-image relative overflow-hidden rounded-2xl" style={{
            opacity: 0
          }}>
              <div className="relative w-full" style={{
              aspectRatio: "4 / 5",
              minHeight: "clamp(400px, 60vh, 700px)"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1737412/img/IMG_5134.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1737412/img/IMG_5134.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1737412/img/IMG_5134.jpg-wvc-sizes" loading="lazy" alt="Professional car detailing service" className="absolute inset-0 h-full w-full object-cover" />
              </div>
            </div>
          </div>

          {}
          <div className="relative lg:col-span-6">
            {}
            <svg aria-hidden="true" className="pointer-events-none absolute inset-0 -z-0 h-full w-full" viewBox="0 0 600 700" preserveAspectRatio="none" fill="none">
              <g stroke="hsl(41 62% 62%)" strokeOpacity="0.06" strokeWidth="1" fill="none">
                <path d="M-50,120 C120,80 260,180 420,140 C520,115 600,150 700,130" />
                <path d="M-50,200 C130,160 280,260 440,220 C540,195 610,230 720,210" />
                <path d="M-50,290 C140,250 300,350 460,310 C560,285 620,320 730,300" />
                <path d="M-50,380 C150,340 320,440 480,400 C580,375 630,410 740,390" />
                <path d="M-50,470 C160,430 340,530 500,490 C600,465 640,500 750,480" />
                <path d="M-50,560 C170,520 360,620 520,580 C620,555 650,590 760,570" />
                <path d="M-50,650 C180,610 380,710 540,670 C640,645 660,680 770,660" />
              </g>
            </svg>

            <div className="relative z-10 max-w-[480px] mx-auto lg:mx-0">
              <h2 className="pairing-reveal font-serif" style={{
              opacity: 0,
              fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.015em",
              fontWeight: 400,
              color: "var(--foreground)"
            }}>
                Professional detailing built on{" "}
                <span className="relative inline-block italic">
                  <span className="pairing-italic-glow pointer-events-none absolute inset-0 -z-10" style={{
                  opacity: 0,
                  background: "radial-gradient(ellipse at center, hsl(41 62% 62% / 0.35) 0%, transparent 70%)",
                  filter: "blur(20px)",
                  transform: "scale(1.4)"
                }} aria-hidden="true" />
                  precision
                </span>
                .
              </h2>

              <div className="mt-10 space-y-6">
                <p className="pairing-reveal font-default" style={{
                opacity: 0,
                fontSize: "clamp(1rem, 1vw, 1.0625rem)",
                lineHeight: 1.7,
                color: "var(--muted-foreground)"
              }}>
                  Valero Elite Detailing provides professional mobile vehicle care throughout Miami. We bring our own equipment, water, professional-grade products, and attention to detail directly to your home or office.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}